import React, { useEffect, useState } from "react";
import Logo from "components/Logo/Logo";
import MenuBar from "components/MenuBar/MenuBar";
import Navigation from "components/Navigation/Navigation";
import { useDispatch, useSelector } from "react-redux";
import { logout, selectConnectedUser } from "../../redux/userSlice";
import { useNavigate } from "react-router-dom";
import { User } from "lucide-react";
import { Buffer } from "buffer";

const MainNav2Logged = () => {
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);

  const user = useSelector(selectConnectedUser); // selector sécurisé
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Gestion du resize
  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    setMenuOpen(false);
    navigate("/login");
  };

  // ================= RETINA AVATAR PREVIEW =================
  useEffect(() => {
    if (!user?.img?.data) return;

    const img = new Image();
    img.src = `data:${user.img.contentType};base64,${Buffer.from(
      user.img.data
    ).toString("base64")}`;

    img.onload = () => {
      const displaySize = 40; // taille CSS
      const scale = window.devicePixelRatio || 1; // retina
      const canvasSize = displaySize * scale;

      const canvas = document.createElement("canvas");
      canvas.width = canvasSize;
      canvas.height = canvasSize;

      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvasSize, canvasSize);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      // Ratio pour remplir le carré sans déformation
      const ratio = Math.max(canvasSize / img.width, canvasSize / img.height);
      const newWidth = img.width * ratio;
      const newHeight = img.height * ratio;
      const dx = (canvasSize - newWidth) / 2;
      const dy = (canvasSize - newHeight) / 2;

      ctx.drawImage(img, dx, dy, newWidth, newHeight);

      setAvatarPreview(canvas.toDataURL("image/png"));
    };
  }, [user]);

  // ================= RENDER AVATAR =================
  const renderAvatar = () => {
    if (avatarPreview) {
      return (
        <img
          src={avatarPreview}
          alt="Avatar"
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            width: "40px",
            height: "40px",
            objectFit: "cover",
            borderRadius: "50%",
            cursor: "pointer",
          }}
        />
      );
    }

    return (
      <User
        size={32}
        onClick={() => setMenuOpen(!menuOpen)}
        className="cursor-pointer text-gray-700 dark:text-white"
      />
    );
  };

  // ================= MENU UTILISATEUR =================
  const renderUserMenu = () => (
    <div className="absolute right-4 top-16 bg-white border rounded shadow-md w-56 z-50 text-sm">
      {user && user.role !== "user" ? (
        <>
          <button
            onClick={() => {
              navigate("/admin/profil");
              setMenuOpen(false);
            }}
            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
          >
            Profil
          </button>

          {(user.role === "membre" || user.role === "editor" || user.role === "admin") && (
            <>
              <button
                onClick={() => {
                  navigate("/admin");
                  setMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Panneau admin
              </button>
              <button
                onClick={() => {
                  navigate("/admin/listeProjets");
                  setMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Liste des projets
              </button>
              <button
                onClick={() => {
                  navigate("/admin/ajouterProjet");
                  setMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Ajouter un projet
              </button>
              <button
                onClick={() => {
                  navigate("/admin/sommaire");
                  setMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Sommaire des votes
              </button>
            </>
          )}

          {user.role === "admin" && (
            <button
              onClick={() => {
                navigate("/admin/gestionUsagers");
                setMenuOpen(false);
              }}
              className="block w-full text-left px-4 py-2 hover:bg-gray-100"
            >
              Gestion usagers
            </button>
          )}

          {(user.role === "admin" || user.role === "editor") && (
            <>
              <button
                onClick={() => {
                  navigate("/admin/ajouterArticle");
                  setMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Ajouter article
              </button>
              <button
                onClick={() => {
                  navigate("/admin/modifierArticle");
                  setMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Modifier article
              </button>
               <button
                onClick={() => {
                  navigate("/admin/creerInfolettre");
                  setMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Créer infolettre
              </button>
              <button
                onClick={() => {
                  navigate("/admin/ajouterInfolettre");
                  setMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Ajouter infolettre
              </button>
              <button
                onClick={() => {
                  navigate("/admin/envoyerInfolettre");
                  setMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Envoyer infolettre
              </button>
              <button
                onClick={() => {
                  navigate("/admin/ajouterVideo");
                  setMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Ajouter vidéo
              </button>
              <button
                onClick={() => {
                  navigate("/ajouterEvenement");
                  setMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Ajouter événement
              </button>
              <button
                onClick={() => {
                  navigate("/supprimerEvenement");
                  setMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Supprimer événement
              </button>
              <button
                onClick={() => {
                  navigate("/commentaires");
                  setMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Commentaires
              </button>
            </>
          )}

          <button
            onClick={handleLogout}
            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
          >
            Déconnexion
          </button>
        </>
      ) : (
        <button
          onClick={() => {
            navigate("/login");
            setMenuOpen(false);
          }}
          className="block w-full text-left px-4 py-2 hover:bg-gray-100"
        >
          Connexion
        </button>
      )}
    </div>
  );

  // ================= RENDER CONTENT =================
  const renderContent = () => {
    if (dimensions.width < 1000) {
      return (
        <div className="h-20 flex justify-between items-center">
          <MenuBar />
          <Logo />
          <div className="relative">{renderAvatar()}</div>
        </div>
      );
    }

    return (
      <div className="h-20 flex justify-between items-center">
        <Logo />
        <div className="flex-[2] flex justify-center mx-4">
          <Navigation />
        </div>
        <div className="relative">{renderAvatar()}</div>
      </div>
    );
  };

  return (
    <div className="nc-MainNav2Logged relative z-10 bg-white dark:bg-neutral-900 border-b border-slate-100 dark:border-slate-700">
      <div className="w-full px-4">{renderContent()}</div>
      {menuOpen && renderUserMenu()}
    </div>
  );
};

export default MainNav2Logged;
